<div class="home-folders-accordion-container">
  <!-- Loading State -->
  <div *ngIf="loading"
       class="loading-container">
    <mat-progress-spinner color="warn"
                          [diameter]="40"
                          [strokeWidth]="4"></mat-progress-spinner>
    <p *ngIf="loadingMessage"
       class="loading-message">
      {{loadingMessage}}
    </p>
  </div>

  <!-- Expandable Folders List -->
  <div *ngIf="!loading && displayFolders.length > 0"
       class="folders-list">
    <ng-container *ngFor="let folder of displayFolders; let i = index">
      <!-- Render folder and its children recursively -->
      <ng-container *ngTemplateOutlet="folderTemplate; context: { $implicit: folder, level: 0 }"></ng-container>
    </ng-container>
  </div>

  <!-- Folder Template for Recursive Rendering -->
  <ng-template #folderTemplate
               let-folder
               let-level="level">
    <div *ngIf="level <= 1"
         #folderItem
         class="folder-item"
         [ngClass]="{ 
           'expanded': isExpanded(folder),
           'index-folder': folder.name === 'INDEX',
           'subfolder': level > 0,
           'hub-child': folder.parentId && level > 0
         }"
         [style.margin-left]="getFolderIndent(folder)">

      <!-- Folder Header -->
      <!-- Should not show if the level is greater than 1 -->
      <div class="folder-header"
           [ngClass]="{ 
             'expanded': isExpanded(folder),
             'hub-selected': folder.type === 'hub' && isHubSelected(folder)
           }"
           (click)="onFolderClick(folder, $event)"
           (mousedown)="onFolderMouseDown(folder, $event)">
        <div class="folder-info">
          <div class="folder-title-group">
            <mat-icon class="folder-icon"
                      [ngStyle]="{ color: getFolderColor(folder) }">
              {{ getFolderIcon(folder) }}
            </mat-icon>
            {{titleText}}
            <span class="folder-name">
              {{folder.name}}
            </span>
            <span *ngIf="folder.count && folder.type !== 'hub'"
                  class="folder-count">
              ({{ folder.count }})
            </span>
            <span *ngIf="folder.type === 'hub'"
                  class="hub-indicator"
                  [ngClass]="{ 'hub-selected': isHubSelected(folder) }">
              {{ isHubSelected(folder) ? '(Selected Hub)' : '(Hub)' }}
            </span>
          </div>
          <mat-icon *ngIf="folder.hasNotifications"
                    class="blinking-bell notification-icon">
            fiber_manual_record
          </mat-icon>
        </div>

        <!-- Action Buttons -->
        <div class="folder-actions">
          <!-- Removed search results button - not needed for HUB-only functionality -->

          <!-- Refresh Button -->
          <button *ngIf="isExpanded(folder)"

                  mat-icon-button
                  matTooltip="Refresh folder contents"

                  [disabled]="isRefreshing(folder)"
                  (click)="onRefreshFolder(folder, $event)">
            <mat-icon *ngIf="!isRefreshing(folder)">
              refresh
            </mat-icon>
            <mat-progress-spinner *ngIf="isRefreshing(folder)"
                                  [diameter]="20"
                                  [strokeWidth]="2">
            </mat-progress-spinner>
          </button>

          <!-- Delete Folder Button -->
          <button *ngIf="!isPredefinedFolder(folder) && !folder.predefined && permissions?.deletefiles"
                  class="delete-folder-button"
                  mat-button
                  matTooltip="Delete folder"
                  (click)="onDeleteFolder(folder, $event)">
            <mat-icon>
              delete
            </mat-icon>
            Delete Folder
          </button>

          <!-- Expansion Icon -->
          <mat-icon class="expansion-icon">
            {{ isExpanded(folder) ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </mat-icon>
        </div>
      </div>

      <!-- Children folders (for hub folders) - Show as Material Design tabs -->
      <div *ngIf="folder.children && folder.children.length > 0 && isExpanded(folder) && !isSingleChildMode(folder)"
           class="children-tabs-container">
        <mat-tab-group [selectedIndex]="getSelectedTabIndex(folder.id)"
                       (selectedTabChange)="onTabChange(folder.id, $event)">
          <mat-tab *ngFor="let child of folder.children; trackBy: trackByFolderId"
                   [label]="getTabLabel(child)"
                   (click)="onTabClick(child, $event)">
            <!-- Optionally add an upload icon indicator -->
            <ng-template mat-tab-label>
              <span>
                {{getTabLabel(child)}}
              </span>
              <mat-icon *ngIf="child.upload_folder"
                        class="upload-indicator"
                        style="font-size: 16px; margin-left: 5px;"
                        matTooltip="Upload enabled">
                cloud_upload
              </mat-icon>
            </ng-template>
            <ng-template matTabContent>
              <!-- Tab content: Files Table for this child folder -->
              <div class="tab-content">
                <!-- Loading State for Tab Content -->
                <div *ngIf="child.loading"
                     class="folder-loading">
                  <mat-progress-spinner color="warn"
                                        [diameter]="20"
                                        [strokeWidth]="3"></mat-progress-spinner>
                  <span>
                    Loading {{ child.name }} files...
                  </span>
                </div>

                <!-- Files Table for Tab Content -->
                <div *ngIf="!child.loading && child.files && child.files.length > 0"
                     class="files-table-container">
                  <div class="files-table">
                    <table class="accordion-table">
                      <!-- Table Header -->
                      <thead>
                        <tr>
                          <th class="file-name-column">
                            File Name
                          </th>
                          <th class="file-size-column">
                            File Size
                          </th>
                          <th class="uploaded-date-column">
                            Date Uploaded
                          </th>
                          <th class="date-of-file-column">
                            Date of File
                          </th>
                        </tr>
                      </thead>

                      <!-- Table Body -->
                      <tbody>
                        <!-- Regular files -->
                        <tr *ngFor="let file of getPaginatedFiles(child); trackBy: trackByFileId"
                            class="file-row"
                            [ngClass]="{ 'selected': isFileSelected(child, file) }">

                          <!-- File Name Column -->
                          <td class="file-name-column">
                            <div class="file-name-content">
                              <!-- DICOMDIR/Imaging Study with special appearance -->
                              <ng-container *ngIf="isDicomFile(file); else regularFile">
                                <span class="imaging-study-label"
                                      (click)="onFileClick(file, $event)">
                                  <mat-icon class="imaging-study-icon">
                                    visibility
                                  </mat-icon>
                                  Imaging Study
                                </span>
                              </ng-container>
                              <ng-template #regularFile>
                                <button *ngIf="file.viewerurl && !isInactiveStore(file)"
                                        class="view-link"
                                        matTooltip="Click here to browse the study in the DICOM viewer."
                                        (click)="onStudyClick(file, $event)">
                                  <mat-icon aria-hidden="false"
                                            aria-label="preview icon">
                                    preview
                                  </mat-icon>
                                  {{ file.fileName }}
                                </button>
                                <div *ngIf="!file.viewerurl || isInactiveStore(file)"
                                     class="regular-file"
                                     (click)="onFileClick(file, $event)">
                                  <mat-icon class="file-icon">
                                    {{ file.type === 'folder' ? 'folder' : 'description' }}
                                  </mat-icon>
                                  <span class="file-name">
                                    {{file.fileName}}
                                  </span>
                                </div>
                              </ng-template>
                              <mat-icon *ngIf="file.hasNotifications"
                                        class="blinking-bell notification-icon">
                                fiber_manual_record
                              </mat-icon>
                              <mat-icon *ngIf="file.hasCommentAttachments"
                                        class="comment-attachment-icon"
                                        matTooltip="This file has comment attachments."
                                        (click)="onAddCommentOrAttachment(file, $event)">
                                comment
                              </mat-icon>
                            </div>
                          </td>

                          <!-- File Size Column -->
                          <td class="file-size-column">
                            {{ file.fileSize | fileSize }}
                          </td>

                          <!-- Date Uploaded Column -->
                          <td class="uploaded-date-column"
                              matTooltipPosition="above"
                              [matTooltip]="file.uploadedDate || 'No date available'">
                            {{ formatDate(file.uploadedDate) }}
                          </td>

                          <!-- Date of File Column -->
                          <td class="date-of-file-column"
                              matTooltipPosition="above"
                              [matTooltip]="file.fdate ? (file.ftime ? file.fdate + ' ' + file.ftime : file.fdate) : 'No date available'">
                            {{ formatFileDateTime(file.fdate, file.ftime) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <!-- Pagination Controls -->
                    <div *ngIf="getPaginationState(child.id).totalPages > 1"
                         class="pagination-controls">
                      <div class="page-size-controls">
                        <label for="pageSize-{{child.id}}">
                          Items per page:
                        </label>
                        <select #pageSizeSelect
                                id="pageSize-{{child.id}}"
                                [value]="getPaginationState(child.id).pageSize"
                                (change)="onPageSizeChange(child.id, +pageSizeSelect.value)">
                          <option *ngFor="let size of getPageSizeOptions()"
                                  [value]="size">
                            {{size}}
                          </option>
                        </select>
                      </div>

                      <!-- Pagination Navigation -->
                      <div class="pagination-navigation">
                        <button class="pagination-btn"
                                matTooltip="First page"
                                [disabled]="getPaginationState(child.id).currentPage === 1"
                                (click)="onPageChange(child.id, 1)">
                          <mat-icon>
                            first_page
                          </mat-icon>
                        </button>
                        <button class="pagination-btn"
                                matTooltip="Previous page"
                                [disabled]="getPaginationState(child.id).currentPage === 1"
                                (click)="onPageChange(child.id, getPaginationState(child.id).currentPage - 1)">
                          <mat-icon>
                            chevron_left
                          </mat-icon>
                        </button>
                        <span class="page-info">
                          Page {{ getPaginationState(child.id).currentPage }} of {{ getPaginationState(child.id).totalPages }}
                        </span>
                        <button class="pagination-btn"
                                matTooltip="Next page"
                                [disabled]="getPaginationState(child.id).currentPage === getPaginationState(child.id).totalPages"
                                (click)="onPageChange(child.id, getPaginationState(child.id).currentPage + 1)">
                          <mat-icon>
                            chevron_right
                          </mat-icon>
                        </button>
                        <button class="pagination-btn"
                                matTooltip="Last page"
                                [disabled]="getPaginationState(child.id).currentPage === getPaginationState(child.id).totalPages"
                                (click)="onPageChange(child.id, getPaginationState(child.id).totalPages)">
                          <mat-icon>
                            last_page
                          </mat-icon>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Empty state when not loading and no files loaded yet -->
                  <div *ngIf="!child.loading && !child.files"
                       class="files-list">
                    <div class="no-files">
                      <p>
                        Click to load folder contents
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Empty state when tab content has no files -->
                <div *ngIf="!child.loading && child.files && child.files.length === 0"
                     class="empty-folder-container">
                  <div class="empty-folder-content">
                    <p class="empty-folder-message">
                      No files listed in {{ getFolderDisplayName(child) }}
                    </p>
                  </div>
                </div>
              </div>
            </ng-template>
          </mat-tab>
        </mat-tab-group>
      </div>

      <!-- Folder Content (Files Table) -->
      <div *ngIf="isExpanded(folder)"
           class="folder-content">

        <!-- Single-child-in-hub rendering: show child's content directly without folder header -->
        <ng-container *ngIf="isSingleChildMode(folder)">
          <ng-container [ngTemplateOutlet]="singleChildContent"
                        [ngTemplateOutletContext]="{ child: getSingleChild(folder) }"></ng-container>
        </ng-container>

        <!-- Empty state when an expanded hub has no child folders (normal mode) -->
        <div *ngIf="!isSingleChildMode(folder) && folder.type === 'hub' && !folder.loading && (!folder.children || folder.children.length === 0)"
             class="empty-folder-container">
          <div class="empty-folder-content">
            <p class="empty-folder-message">
              No folders available for {{ getFolderDisplayName(folder) }}
            </p>
          </div>
        </div>

        <!-- Loading State for Folder (normal mode) -->
        <div *ngIf="!isSingleChildMode(folder) && folder.loading"
             class="folder-loading">
          <mat-progress-spinner color="warn"
                                [diameter]="20"
                                [strokeWidth]="3"></mat-progress-spinner>
          <span>
            Loading files...
          </span>
        </div>

        <!-- Files Table (normal mode) -->
        <div *ngIf="!isSingleChildMode(folder) && !folder.loading && folder.files && folder.files.length > 0"
             class="files-table-container">

          <div class="files-table">
            <table class="accordion-table">
              <!-- Table Header -->
              <thead>
                <tr>
                  <th class="file-name-column">
                    File Name
                  </th>
                  <th class="file-size-column">
                    File Size
                  </th>
                  <th class="uploaded-date-column">
                    Date Uploaded
                  </th>
                  <th class="date-of-file-column">
                    Date of File
                  </th>
                </tr>
              </thead>

              <!-- Table Body -->
              <tbody>
                <!-- Subfolders as file-like items -->
                <ng-container *ngFor="let subfolder of getChildren(folder); trackBy: trackByFolderId">
                  <!-- Subfolder Row -->
                  <tr class="file-row subfolder-row"
                      [ngClass]="{ 'selected': isSubfolderExpanded(subfolder) }">

                    <!-- Subfolder Name Column -->
                    <td class="file-name-column">
                      <div class="file-name-content">
                        <div class="subfolder-item"
                             (click)="onSubfolderClick(subfolder, $event)">
                          <button class="subfolder-expand-button"
                                  mat-icon-button
                                  [matTooltip]="isSubfolderExpanded(subfolder) ? 'Collapse subfolder' : 'Expand subfolder'"
                                  (click)="toggleSubfolderExpansion(subfolder, $event)">
                            <mat-icon class="subfolder-expand-icon">
                              {{ isSubfolderExpanded(subfolder) ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                            </mat-icon>
                          </button>
                          <div class="subfolder-content">
                            <mat-icon class="subfolder-icon">
                              folder
                            </mat-icon>
                            <span class="subfolder-name">
                              {{ subfolder.name }}
                            </span>
                            <span *ngIf="subfolder.count"
                                  class="subfolder-count">
                              ({{ subfolder.count }})
                            </span>
                            <mat-icon *ngIf="subfolder.hasNotifications"
                                      class="blinking-bell notification-icon">
                              fiber_manual_record
                            </mat-icon>
                          </div>
                        </div>
                      </div>
                    </td>

                    <!-- Empty columns for subfolder row -->
                    <td class="file-size-column">
                      -
                    </td>
                    <td class="uploaded-date-column">
                      -
                    </td>
                    <td class="date-of-file-column">
                      -
                    </td>
                  </tr>

                  <!-- Subfolder Files (nested within the same table) -->
                  <ng-container *ngIf="isSubfolderExpanded(subfolder)">
                    <!-- Loading State for Subfolder -->
                    <tr *ngIf="subfolder.loading"
                        class="subfolder-loading-row">
                      <td class="subfolder-loading-cell"
                          colspan="4">
                        <div class="subfolder-loading">
                          <mat-progress-spinner color="warn"
                                                [diameter]="20"
                                                [strokeWidth]="3"></mat-progress-spinner>
                          <span>
                            Loading {{ subfolder.name }} files...
                          </span>
                        </div>
                      </td>
                    </tr>

                    <!-- Subfolder Files -->
                    <tr *ngFor="let file of getPaginatedFiles(subfolder); trackBy: trackByFileId"
                        class="file-row subfolder-file-row"
                        [ngClass]="{ 'selected': isFileSelected(subfolder, file) }">

                      <!-- File Name Column -->
                      <td class="file-name-column">
                        <div class="file-name-content subfolder-file-content">
                          <ng-container *ngIf="isDicomFile(file); else regularFile">
                            <span class="imaging-study-label"
                                  (click)="onFileClick(file, $event)">
                              <mat-icon class="imaging-study-icon">
                                visibility
                              </mat-icon>
                              Imaging Study
                            </span>
                          </ng-container>
                          <ng-template #regularFile>
                            <button *ngIf="file.viewerurl && !isInactiveStore(file)"
                                    class="view-link"
                                    matTooltip="Click here to browse the study in the DICOM viewer."
                                    (click)="onStudyClick(file, $event)">
                              <mat-icon aria-hidden="false"
                                        aria-label="preview icon">
                                preview
                              </mat-icon>
                              {{ file.fileName }}
                            </button>
                            <div *ngIf="!file.viewerurl || isInactiveStore(file)"
                                 class="regular-file"
                                 (click)="onFileClick(file, $event)">
                              <mat-icon class="file-icon">
                                {{ file.type === 'folder' ? 'folder' : 'description' }}
                              </mat-icon>
                              <span class="file-name">
                                {{file.fileName}}
                              </span>
                            </div>
                          </ng-template>
                          <mat-icon *ngIf="file.hasNotifications"
                                    class="blinking-bell notification-icon">
                            fiber_manual_record
                          </mat-icon>
                          <mat-icon *ngIf="file.hasCommentAttachments"
                                    class="comment-attachment-icon"
                                    matTooltip="This file has comment attachments."
                                    (click)="onAddCommentOrAttachment(file, $event)">
                            comment
                          </mat-icon>
                        </div>
                      </td>

                      <!-- File Size Column -->
                      <td class="file-size-column">
                        {{ file.fileSize | fileSize }}
                      </td>

                      <!-- Date Uploaded Column -->
                      <td class="uploaded-date-column"
                          matTooltipPosition="above"
                          [matTooltip]="file.uploadedDate || 'No date available'">
                        {{ formatDate(file.uploadedDate) }}
                      </td>

                      <!-- Date of File Column -->
                      <td class="date-of-file-column"
                          matTooltipPosition="above"
                          [matTooltip]="file.fdate ? (file.ftime ? file.fdate + ' ' + file.ftime : file.fdate) : 'No date available'">
                        {{ formatFileDateTime(file.fdate, file.ftime) }}
                      </td>
                    </tr>

                    <!-- Empty state when subfolder is expanded but has no files -->
                    <tr *ngIf="!subfolder.loading && subfolder.files && subfolder.files.length === 0"
                        class="empty-subfolder-row">
                      <td class="empty-subfolder-cell"
                          colspan="4">
                        <div class="empty-subfolder-content">
                          <p class="empty-subfolder-message">
                            No files listed in {{ subfolder.name }}
                          </p>
                        </div>
                      </td>
                    </tr>
                  </ng-container>
                </ng-container>

                <!-- Regular files -->
                <tr *ngFor="let file of getPaginatedFiles(folder); trackBy: trackByFileId"
                    class="file-row"
                    [ngClass]="{ 'selected': isFileSelected(folder, file) }">

                  <!-- File Name Column -->
                  <td class="file-name-column">
                    <div class="file-name-content">
                      <!-- DICOMDIR/Imaging Study with special appearance -->
                      <ng-container *ngIf="isDicomFile(file); else regularFile">
                        <span class="imaging-study-label"
                              (click)="onFileClick(file, $event)">
                          <mat-icon class="imaging-study-icon">
                            visibility
                          </mat-icon>
                          Imaging Study
                        </span>
                      </ng-container>
                      <ng-template #regularFile>
                        <button *ngIf="file.viewerurl && !isInactiveStore(file)"
                                class="view-link"
                                matTooltip="Click here to browse the study in the DICOM viewer."
                                (click)="onStudyClick(file, $event)">
                          <mat-icon aria-hidden="false"
                                    aria-label="preview icon">
                            preview
                          </mat-icon>
                          {{ file.fileName }}
                        </button>
                        <div *ngIf="!file.viewerurl || isInactiveStore(file)"
                             class="regular-file"
                             (click)="onFileClick(file, $event)">
                          <mat-icon class="file-icon">
                            {{ file.type === 'folder' ? 'folder' : 'description' }}
                          </mat-icon>
                          <span class="file-name">
                            {{ file.fileName }}
                          </span>
                        </div>
                      </ng-template>
                      <mat-icon *ngIf="file.hasNotifications"
                                class="blinking-bell notification-icon">
                        fiber_manual_record
                      </mat-icon>
                      <mat-icon *ngIf="file.hasCommentAttachments"
                                class="comment-attachment-icon"
                                matTooltip="This file has comment attachments."
                                (click)="onAddCommentOrAttachment(file, $event)">
                        comment
                      </mat-icon>
                    </div>
                  </td>

                  <!-- File Size Column -->
                  <td class="file-size-column">
                    {{ file.fileSize | fileSize }}
                  </td>


                  <!-- Date Uploaded Column -->
                  <td class="uploaded-date-column"
                      matTooltipPosition="above"
                      [matTooltip]="file.uploadedDate || 'No date available'">
                    {{ formatDate(file.uploadedDate) }}
                  </td>

                  <!-- Date of File Column -->
                  <td class="date-of-file-column"
                      matTooltipPosition="above"
                      [matTooltip]="file.fdate ? (file.ftime ? file.fdate + ' ' + file.ftime : file.fdate) : 'No date available'">
                    {{ formatFileDateTime(file.fdate, file.ftime) }}
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Pagination Controls -->
            <div *ngIf="getPaginationState(folder.id).totalPages > 1"
                 class="pagination-controls">
              <div class="page-size-controls">
                <label for="pageSize-{{folder.id}}">
                  Items per page:
                </label>
                <select #pageSizeSelect
                        id="pageSize-{{folder.id}}"
                        [value]="getPaginationState(folder.id).pageSize"
                        (change)="onPageSizeChange(folder.id, +pageSizeSelect.value)">
                  <option *ngFor="let size of getPageSizeOptions()"
                          [value]="size">
                    {{ size }}
                  </option>
                </select>
              </div>

              <!-- Pagination Navigation -->
              <div class="pagination-navigation">
                <button class="pagination-btn"
                        matTooltip="First page"
                        [disabled]="getPaginationState(folder.id).currentPage === 1"
                        (click)="onPageChange(folder.id, 1)">
                  <mat-icon>
                    first_page
                  </mat-icon>
                </button>

                <button class="pagination-btn"
                        matTooltip="Previous page"
                        [disabled]="getPaginationState(folder.id).currentPage === 1"
                        (click)="onPageChange(folder.id, getPaginationState(folder.id).currentPage - 1)">
                  <mat-icon>
                    chevron_left
                  </mat-icon>
                </button>

                <span class="page-info">
                  Page {{ getPaginationState(folder.id).currentPage }} of {{ getPaginationState(folder.id).totalPages }}
                </span>

                <button class="pagination-btn"
                        matTooltip="Next page"
                        [disabled]="getPaginationState(folder.id).currentPage === getPaginationState(folder.id).totalPages"
                        (click)="onPageChange(folder.id, getPaginationState(folder.id).currentPage + 1)">
                  <mat-icon>
                    chevron_right
                  </mat-icon>
                </button>

                <button class="pagination-btn"
                        matTooltip="Last page"
                        [disabled]="getPaginationState(folder.id).currentPage === getPaginationState(folder.id).totalPages"
                        (click)="onPageChange(folder.id, getPaginationState(folder.id).totalPages)">
                  <mat-icon>
                    last_page
                  </mat-icon>
                </button>
              </div>
            </div>
          </div>

          <!-- Empty state when not loading and no files loaded yet (normal mode) -->
          <div *ngIf="!isSingleChildMode(folder) && !folder.loading && !folder.files"
               class="files-list">
            <div class="no-files">
              <p>
                Click to load folder contents
              </p>
            </div>
          </div>
        </div>


        <!-- Empty state when folder is expanded but has no files (normal mode) -->
        <div *ngIf="!isSingleChildMode(folder) && !folder.loading && folder.files && folder.files.length === 0"
             class="empty-folder-container">
          <div class="empty-folder-content">
            <!-- Empty folder message -->
            <p class="empty-folder-message">
              No files listed in {{ getFolderDisplayName(folder) }}
            </p>
          </div>
        </div>
      </div>

    </div>
  </ng-template>

  <!-- Template to render single child folder content directly under a hub -->
  <ng-template #singleChildContent
               let-child="child">
    <!-- Loading State for Child Folder -->
    <div *ngIf="child.loading"
         class="folder-loading">
      <mat-progress-spinner color="warn"
                            [diameter]="20"
                            [strokeWidth]="3"></mat-progress-spinner>
      <span>
        Loading files...
      </span>
    </div>

    <!-- Files Table for Child -->
    <div *ngIf="!child.loading && child.files && child.files.length > 0"
         class="files-table-container">

      <div class="files-table">
        <table class="accordion-table">
          <!-- Table Header -->
          <thead>
            <tr>
              <th class="file-name-column">
                File Name
              </th>
              <th class="file-size-column">
                File Size
              </th>
              <th class="uploaded-date-column">
                Date Uploaded
              </th>
              <th class="date-of-file-column">
                Date of File
              </th>
            </tr>
          </thead>

          <!-- Table Body -->
          <tbody>
            <!-- Subfolders as file-like items -->
            <ng-container *ngFor="let subfolder of getChildren(child); trackBy: trackByFolderId">
              <!-- Subfolder Row -->
              <tr class="file-row subfolder-row"
                  [ngClass]="{ 'selected': isSubfolderExpanded(subfolder) }">

                <!-- Subfolder Name Column -->
                <td class="file-name-column">
                  <div class="file-name-content">
                    <div class="subfolder-item"
                         (click)="onSubfolderClick(subfolder, $event)">
                      <button class="subfolder-expand-button"
                              mat-icon-button
                              [matTooltip]="isSubfolderExpanded(subfolder) ? 'Collapse subfolder' : 'Expand subfolder'"
                              (click)="toggleSubfolderExpansion(subfolder, $event)">
                        <mat-icon class="subfolder-expand-icon">
                          {{ isSubfolderExpanded(subfolder) ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                        </mat-icon>
                      </button>
                      <div class="subfolder-content">
                        <mat-icon class="subfolder-icon">
                          folder
                        </mat-icon>
                        <span class="subfolder-name">
                          {{subfolder.name}}
                        </span>
                        <span *ngIf="subfolder.count"
                              class="subfolder-count">
                          ({{ subfolder.count }})
                        </span>
                        <mat-icon *ngIf="subfolder.hasNotifications"
                                  class="blinking-bell notification-icon">
                          fiber_manual_record
                        </mat-icon>
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Empty columns for subfolder row -->
                <td class="file-size-column">
                  -
                </td>
                <td class="uploaded-date-column">
                  -
                </td>
                <td class="date-of-file-column">
                  -
                </td>
              </tr>

              <!-- Subfolder Files (nested within the same table) -->
              <ng-container *ngIf="isSubfolderExpanded(subfolder)">
                <!-- Loading State for Subfolder -->
                <tr *ngIf="subfolder.loading"
                    class="subfolder-loading-row">
                  <td class="subfolder-loading-cell"
                      colspan="4">
                    <div class="subfolder-loading">
                      <mat-progress-spinner color="warn"
                                            [diameter]="20"
                                            [strokeWidth]="3"></mat-progress-spinner>
                      <span>
                        Loading {{ subfolder.name }} files...
                      </span>
                    </div>
                  </td>
                </tr>

                <!-- Subfolder Files -->
                <tr *ngFor="let file of getPaginatedFiles(subfolder); trackBy: trackByFileId"
                    class="file-row subfolder-file-row"
                    [ngClass]="{ 'selected': isFileSelected(subfolder, file) }">
                  <td class="file-name-column">
                    <div class="file-name-content subfolder-file-content">
                      <ng-container *ngIf="isDicomFile(file); else regularSubFile">
                        <span class="imaging-study-label"
                              (click)="onFileClick(file, $event)">
                          <mat-icon class="imaging-study-icon">
                            visibility
                          </mat-icon>
                          Imaging Study
                        </span>
                      </ng-container>
                      <ng-template #regularSubFile>
                        <button *ngIf="file.viewerurl && !isInactiveStore(file)"
                                class="view-link"
                                matTooltip="Click here to browse the study in the DICOM viewer."
                                (click)="onStudyClick(file, $event)">
                          <mat-icon aria-hidden="false"
                                    aria-label="preview icon">
                            preview
                          </mat-icon>
                          {{ file.fileName }}
                        </button>
                        <div *ngIf="!file.viewerurl || isInactiveStore(file)"
                             class="regular-file"
                             (click)="onFileClick(file, $event)">
                          <mat-icon class="file-icon">
                            {{file.type === 'folder' ? 'folder' : 'description'}}
                          </mat-icon>
                          <span class="file-name">
                            {{file.fileName}}
                          </span>
                        </div>
                      </ng-template>
                      <mat-icon *ngIf="file.hasNotifications"
                                class="blinking-bell notification-icon">
                        fiber_manual_record
                      </mat-icon>
                      <mat-icon *ngIf="file.hasCommentAttachments"
                                class="comment-attachment-icon"
                                matTooltip="This file has comment attachments."
                                (click)="onAddCommentOrAttachment(file, $event)">
                        comment
                      </mat-icon>
                    </div>
                  </td>
                  <td class="file-size-column">
                    {{ file.fileSize | fileSize }}
                  </td>
                  <td class="uploaded-date-column"
                      matTooltipPosition="above"
                      [matTooltip]="file.uploadedDate || 'No date available'">
                    {{formatDate(file.uploadedDate)}}
                  </td>
                  <td class="date-of-file-column"
                      matTooltipPosition="above"
                      [matTooltip]="file.fdate ? (file.ftime ? file.fdate + ' ' + file.ftime : file.fdate) : 'No date available'">
                    {{formatFileDateTime(file.fdate, file.ftime)}}
                  </td>
                </tr>

                <!-- Empty state when subfolder is expanded but has no files -->
                <tr *ngIf="!subfolder.loading && subfolder.files && subfolder.files.length === 0"
                    class="empty-subfolder-row">
                  <td class="empty-subfolder-cell"
                      colspan="4">
                    <div class="empty-subfolder-content">
                      <p class="empty-subfolder-message">
                        No files listed in {{ subfolder.name }}
                      </p>
                    </div>
                  </td>
                </tr>
              </ng-container>
            </ng-container>

            <!-- Regular files in child folder -->
            <tr *ngFor="let file of getPaginatedFiles(child); trackBy: trackByFileId"
                class="file-row"
                [ngClass]="{ 'selected': isFileSelected(child, file) }">
              <td class="file-name-column">
                <div class="file-name-content">
                  <ng-container *ngIf="isDicomFile(file); else regularFile">
                    <span class="imaging-study-label"
                          (click)="onFileClick(file, $event)">
                      <mat-icon class="imaging-study-icon">
                        visibility
                      </mat-icon>
                      Imaging Study
                    </span>
                  </ng-container>
                  <ng-template #regularFile>
                    <button *ngIf="file.viewerurl && !isInactiveStore(file)"
                            class="view-link"
                            matTooltip="Click here to browse the study in the DICOM viewer."
                            (click)="onStudyClick(file, $event)">
                      <mat-icon aria-hidden="false"
                                aria-label="preview icon">
                        preview
                      </mat-icon>
                      {{ file.fileName }}
                    </button>
                    <div *ngIf="!file.viewerurl || isInactiveStore(file)"
                         class="regular-file"
                         (click)="onFileClick(file, $event)">
                      <mat-icon class="file-icon">
                        {{file.type === 'folder' ? 'folder' : 'description'}}
                      </mat-icon>
                      <span class="file-name">
                        {{file.fileName}}
                      </span>
                    </div>
                  </ng-template>
                  <mat-icon *ngIf="file.hasNotifications"
                            class="blinking-bell notification-icon">
                    fiber_manual_record
                  </mat-icon>
                  <mat-icon *ngIf="file.hasCommentAttachments"
                            class="comment-attachment-icon"
                            matTooltip="This file has comment attachments."
                            (click)="onAddCommentOrAttachment(file, $event)">
                    comment
                  </mat-icon>
                </div>
              </td>
              <td class="file-size-column">
                {{ file.fileSize | fileSize }}
              </td>
              <td class="uploaded-date-column"
                  matTooltipPosition="above"
                  [matTooltip]="file.uploadedDate || 'No date available'">
                {{formatDate(file.uploadedDate)}}
              </td>
              <td class="date-of-file-column"
                  matTooltipPosition="above"
                  [matTooltip]="file.fdate ? (file.ftime ? file.fdate + ' ' + file.ftime : file.fdate) : 'No date available'">
                {{formatFileDateTime(file.fdate, file.ftime)}}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination Controls for Child -->
        <div *ngIf="getPaginationState(child.id).totalPages > 1"
             class="pagination-controls">
          <div class="page-size-controls">
            <label for="pageSize-{{child.id}}">
              Items per page:
            </label>
            <select #pageSizeSelectChild
                    id="pageSize-{{child.id}}"
                    [value]="getPaginationState(child.id).pageSize"
                    (change)="onPageSizeChange(child.id, +pageSizeSelectChild.value)">
              <option *ngFor="let size of getPageSizeOptions()"
                      [value]="size">
                {{size}}
              </option>
            </select>
          </div>

          <div class="pagination-navigation">
            <button class="pagination-btn"
                    matTooltip="First page"
                    [disabled]="getPaginationState(child.id).currentPage === 1"
                    (click)="onPageChange(child.id, 1)">
              <mat-icon>
                first_page
              </mat-icon>
            </button>
            <button class="pagination-btn"
                    matTooltip="Previous page"
                    [disabled]="getPaginationState(child.id).currentPage === 1"
                    (click)="onPageChange(child.id, getPaginationState(child.id).currentPage - 1)">
              <mat-icon>
                chevron_left
              </mat-icon>
            </button>
            <span class="page-info">
              Page {{ getPaginationState(child.id).currentPage }} of {{ getPaginationState(child.id).totalPages }}
            </span>
            <button class="pagination-btn"
                    matTooltip="Next page"
                    [disabled]="getPaginationState(child.id).currentPage === getPaginationState(child.id).totalPages"
                    (click)="onPageChange(child.id, getPaginationState(child.id).currentPage + 1)">
              <mat-icon>
                chevron_right
              </mat-icon>
            </button>
            <button class="pagination-btn"
                    matTooltip="Last page"
                    [disabled]="getPaginationState(child.id).currentPage === getPaginationState(child.id).totalPages"
                    (click)="onPageChange(child.id, getPaginationState(child.id).totalPages)">
              <mat-icon>
                last_page
              </mat-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty state when not loading and no files loaded yet for child -->
      <div *ngIf="!child.loading && !child.files"
           class="files-list">
        <div class="no-files">
          <p>
            Click to load folder contents
          </p>
        </div>
      </div>
    </div>

    <!-- Empty state when child folder is expanded but has no files -->
    <div *ngIf="!child.loading && child.files && child.files.length === 0"
         class="empty-folder-container">
      <div class="empty-folder-content">
        <p class="empty-folder-message">
          No files listed in {{ getFolderDisplayName(child) }}
        </p>
      </div>
    </div>
  </ng-template>

  <!-- No Folders Message -->
  <div *ngIf="!loading && accordionFolders.length === 0"
       class="no-folders">
    <div class="no-folders-content">
      <mat-icon class="no-folders-icon">
        folder_open
      </mat-icon>
      <h3>
        No folders available
      </h3>
      <p>
        Create your first folder to get started
      </p>
      <button *ngIf="permissions?.createfolder"
              color="warn"
              mat-flat-button
              (click)="onCreateFolder()">
        <mat-icon>
          create_new_folder
        </mat-icon>
        Create First Folder
      </button>
    </div>
  </div>
</div>
